import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  InMemoryCache,
} from "@apollo/client/core";
import { ErrorLink } from "@apollo/client/link/error";

import { reset } from "@application/utilities/reset";
import storage from "@application/utilities/storage";

import { CSRF_TOKEN } from "./csrf";

const APOLLO_LINK = new ApolloLink((operation, forward) => {
  const oid = storage.get("oid");

  const overrides: Record<string, string> = {};
  if (oid) overrides["X-Organization-Id"] = oid;
  if (CSRF_TOKEN) overrides["X-CSRF-Token"] = CSRF_TOKEN;

  operation.setContext(({ headers = {} }) => ({
    headers: { ...headers, ...overrides },
  }));

  return forward(operation);
});

const ERROR_LINK = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    for (const { extensions } of error.errors) {
      if (extensions?.code === "UNAUTHENTICATED") {
        reset();
        break;
      }
    }
  }
});

const HTTP_LINK = new HttpLink({ uri: "/graphql" });

export const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      // No id: a name and version key one row of the Prompts pages.
      PromptBlock: { keyFields: ["name", "version"] },
      Query: {
        fields: {
          // A tool call's dialog reads the call already in the cache: a run's
          // calls carry their results, a chat's don't until the dialog loads.
          projectToolCallRequest: (_, { args, toReference }) =>
            toReference({ __typename: "ProjectToolCallRequest", id: args?.id }),
          systemToolCallRequest: (_, { args, toReference }) =>
            toReference({ __typename: "SystemToolCallRequest", id: args?.id }),
        },
      },
      Project: {
        fields: {
          // stepStates is an embedded object with no id, written by both the
          // list and detail queries — declare a merge so Apollo doesn't warn
          // ("Cache data may be lost when replacing the stepStates field").
          stepStates: { merge: true },
          // Same shape, and the records review asks for a different slice of it
          // than the project screen does. Without a merge each query's write
          // drops the other's fields, so each read misses and goes back to the
          // network — the case page fetched both queries twice on every open.
          recordReview: { merge: true },
          // Same shape: the case Summary query reads `policy { modify }` next
          // to the detail query's full policy.
          policy: { merge: true },
        },
      },
      Conversation: {
        fields: {
          // The chat list reads `policy { destroy }` and the thread `policy
          // { view }`: replacing either hid a new chat or froze the thread.
          policy: { merge: true },
        },
      },
      OrganizationPushable: {
        fields: {
          // The org list reads `summaryFormat { custom }`, the Summary format
          // screen every field.
          summaryFormat: { merge: true },
        },
      },
    },
  }),
  link: ApolloLink.from([APOLLO_LINK, ERROR_LINK, HTTP_LINK]),
});
